'use client';

import { cn } from '@/lib/utils';
/* eslint-disable @next/next/no-img-element */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface LinkTooltipProps {
  url: string;
  number: string;
  sources?: any[]; // 支持多个sources
  indices?: number[]; // 对应的索引
  source?: any; // 兼容单个source
}

const LinkTooltip: React.FC<LinkTooltipProps> = ({ url, sources, indices, source }) => {
  // 处理向后兼容：如果只有source，转换为sources数组
  const allSources = sources || (source ? [source] : []);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const linkRef = useRef<HTMLAnchorElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (showTooltip && linkRef.current && tooltipRef.current) {
      const linkRect = linkRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;

      // 默认显示在上方
      let top = linkRect.top + scrollY - tooltipRect.height - 8;
      let left = linkRect.left + linkRect.width / 2 - tooltipRect.width / 2;

      // 检查是否有足够空间显示在上方
      const spaceAbove = linkRect.top;
      const spaceBelow = viewportHeight - linkRect.bottom;

      // 如果上方空间不够但下方有足够空间，或者链接太靠近顶部
      if (
        (spaceAbove < tooltipRect.height + 8 && spaceBelow > tooltipRect.height + 8) ||
        linkRect.top < 100
      ) {
        // 显示在下方
        top = linkRect.bottom + scrollY + 8;
      }

      // 确保左右不超出视窗
      if (left < 10) {
        left = 10;
      } else if (left + tooltipRect.width > window.innerWidth - 10) {
        left = window.innerWidth - tooltipRect.width - 10;
      }

      setTooltipPosition({ top, left });
    }
  }, [showTooltip]);

  // 获取当前显示的source
  const currentSource = allSources[currentIndex] || allSources[0];
  const currentUrl = currentSource?.metadata?.url || currentSource?.url || url;

  // 提取并简化域名显示
  let siteName = '';
  try {
    const urlObj = new URL(currentUrl);
    const hostname = urlObj.hostname.replace(/^www\./i, '');
    // 去掉顶级域名，只保留主域名
    siteName = hostname.replace(/\.(com|org|net|io|ai|co|dev|app|xyz|info|biz|tv|me|cc)$/i, '');
  } catch (e) {
    siteName = currentUrl?.replace(/.+\/\/|www.|\..+/g, '');
  }
  const title = currentSource?.metadata?.title || currentSource?.title || '';
  const description = currentSource?.pageContent || currentSource?.snippet;

  // 处理鼠标进入
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowTooltip(true);
  };

  // 处理鼠标离开
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
      setCurrentIndex(0); // 重置到第一个
    }, 300); // 300ms 延迟，让用户有时间移动到 tooltip
  };

  // 清理定时器
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <a
        ref={linkRef}
        href={currentUrl}
        target="_blank"
        className={cn(
          'ml-1 text-[10px] h-[18px] no-underline overflow-hidden whitespace-nowrap text-ellipsis rounded-[9px] mr-1 cursor-pointer inline-flex px-2 items-center transition-colors',
          'bg-[#e9e9e9] hover:bg-[#EFEFEF] text-[#757575]',
          'dark:bg-[#202020] dark:hover:bg-[#2c2c2f] text-[#BABABA]',
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {siteName}
        {allSources.length > 1 && (
          <span className="ml-0.5 text-[10px]">+{allSources.length - 1}</span>
        )}
      </a>

      {showTooltip &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tooltipRef}
            className="absolute z-9999"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="bg-bg-main border border-gray-200 dark:border-transparent rounded-[12px] p-3 shadow-lg w-[320px]">
              {/* 顶部导航 - 只在有多个sources时显示 */}
              {allSources.length > 1 && (
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200 dark:border-[#3e3e3e]">
                  {/* 左侧箭头 */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentIndex(
                          currentIndex > 0 ? currentIndex - 1 : allSources.length - 1,
                        );
                      }}
                      className="text-text-secondary hover:text-text-primary p-1"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M10 12L6 8L10 4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="square"
                          strokeLinejoin="miter"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentIndex((currentIndex + 1) % allSources.length);
                      }}
                      className="text-text-secondary hover:text-text-primary p-1"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M6 4L10 8L6 12"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="square"
                          strokeLinejoin="miter"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* 右侧页码 */}
                  <span className="text-text-secondary text-[12px]">
                    {currentIndex + 1}/{allSources.length}
                  </span>
                </div>
              )}
              <div
                className="flex flex-col gap-2.5 cursor-pointer"
                onClick={() => window.open(currentUrl, '_blank')}
              >
                {/* 标题 */}
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2 items-center">
                    <div className="text-text-primary text-[14px] font-medium leading-normal flex-1">
                      {title}
                    </div>
                  </div>
                  <div className="text-text-secondary text-[12px] leading-[16px] truncate-3-lines">
                    {description}
                  </div>
                </div>

                {/* 底部信息 */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    {/* 网站图标 */}
                    <div className="w-4 h-4 rounded-full overflow-hidden border border-gray-300 dark:border-[#3e3e3e] shrink-0">
                      <img
                        src={`https://s2.googleusercontent.com/s2/favicons?domain_url=${currentUrl}`}
                        width={16}
                        height={16}
                        alt="favicon"
                        className="rounded-full h-4 w-4"
                      />
                    </div>
                    {/* 网站名称 */}
                    <span className="text-text-secondary text-[12px] whitespace-nowrap">
                      {siteName.charAt(0).toUpperCase() + siteName.slice(1)}
                    </span>
                    {/* 评分 */}
                    {false && <StarRating />}
                    {/* 信息图标 */}
                    {false && (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8C1.5 11.59 4.41 14.5 8 14.5C11.59 14.5 14.5 11.59 14.5 8C14.5 4.41 11.59 1.5 8 1.5ZM8.625 11.125H7.375V7.375H8.625V11.125ZM8.625 6.125H7.375V4.875H8.625V6.125Z"
                          fill="currentColor"
                          className="text-text-secondary"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default LinkTooltip;

// 评分星星组件
export const StarRating = () => (
  <div className="flex items-center gap-0">
    {[...Array(5)].map((_, i) => (
      <svg
        key={i}
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.72711 2.10366C7.56498 1.77032 7.28998 1.63282 7.00081 1.63282C6.71165 1.63282 6.43665 1.77032 6.27452 2.10366L5.12077 4.4542L2.50744 4.83253C2.19619 4.8742 1.99244 5.06337 1.87077 5.31837C1.74911 5.57337 1.74911 5.88003 1.91244 6.09253L3.74994 7.8442L3.28744 10.4434C3.23161 10.755 3.32202 11.0384 3.52577 11.2492C3.72952 11.46 4.01869 11.5434 4.33577 11.4809L6.99494 10.2467L9.65411 11.4809C9.77577 11.5225 9.90911 11.5434 10.0424 11.5434C10.2533 11.5434 10.4499 11.4809 10.6049 11.36C10.8783 11.1492 10.9966 10.8384 10.9408 10.5267L10.4783 7.9275L12.3158 6.17587C12.5124 5.9792 12.5824 5.69003 12.4816 5.4167C12.3808 5.14337 12.1424 4.93962 11.8108 4.88253L9.19744 4.50412L8.04369 2.15366L7.72711 2.10366Z"
          fill="#FFB800"
        />
      </svg>
    ))}
  </div>
);
